/**
 * MenuUI.js - 基础菜单UI模块
 * 处理主菜单、道具菜单、状态菜单的显示
 */
import ItemSystem from './ItemSystem.js';
import BackgroundHelper from './BackgroundHelper.js';
import UIStyleHelper from './UIStyleHelper.js';

export default class MenuUI {
    constructor(scene) {
        this.scene = scene;
        this.elements = {
            menuList: null,
            infoPanel: null,
            menuTitle: null,
            optionTexts: [],
            categoryTexts: [] // 分类标签文本
        };
    }

    /**
     * 创建菜单背景和标题
     */
    createBackground() {
        // 创建菜单背景（使用高深度值确保显示在最上层）
        const menuBg = this.scene.add.graphics();
        menuBg.fillStyle(0x000000, 0.8);
        menuBg.fillRect(0, 0, this.scene.cameras.main.width, this.scene.cameras.main.height);
        menuBg.setDepth(10000);
        menuBg.setScrollFactor(0); // 确保背景不随相机滚动

        // 添加菜单标题
        this.elements.menuTitle = this.scene.add.text(
            this.scene.cameras.main.centerX,
            20,
            '菜单',
            {
                fontSize: '18px',
                fill: '#ffffff',
                fontFamily: 'monospace'
            }
        );
        this.elements.menuTitle.setOrigin(0.5);
        this.elements.menuTitle.setDepth(10001);
        this.elements.menuTitle.setScrollFactor(0); // 确保标题不随相机滚动
    }

    /**
     * 创建菜单容器
     */
    createContainers() {
        // 创建菜单选项列表（使用高深度值确保显示在最上层）
        this.elements.menuList = this.scene.add.container(
            this.scene.cameras.main.width * 0.25,
            this.scene.cameras.main.height * 0.5
        );
        this.elements.menuList.setDepth(10001);
        this.elements.menuList.setScrollFactor(0); // 确保容器不随相机滚动

        // 创建信息面板（使用高深度值确保显示在最上层）
        this.elements.infoPanel = this.scene.add.container(
            this.scene.cameras.main.width * 0.75,
            this.scene.cameras.main.height * 0.5
        );
        this.elements.infoPanel.setDepth(10001);
        this.elements.infoPanel.setScrollFactor(0); // 确保容器不随相机滚动
    }

    /**
     * 更新菜单标题
     */
    updateTitle(menuType) {
        const titleMap = {
            main: '菜单',
            items: '道具',
            equipment: '装备',
            status: '状态',
            team: '组队'
        };
        if (this.elements.menuTitle) {
            this.elements.menuTitle.setText(titleMap[menuType] || '菜单');
        }
    }

    /**
     * 显示菜单选项
     */
    showMenu(menuType, options, selectedIndex, onItemClick, onItemHover, controller = null) {
        // 恢复容器位置（非装备菜单使用默认位置）
        this.elements.menuList.setPosition(
            this.scene.cameras.main.width * 0.25,
            this.scene.cameras.main.height * 0.5
        );
        this.elements.infoPanel.setPosition(
            this.scene.cameras.main.width * 0.75,
            this.scene.cameras.main.height * 0.5
        );

        // 清空之前的菜单选项
        this.elements.menuList.removeAll(true);
        this.elements.infoPanel.removeAll(true);
        this.elements.optionTexts = [];
        
        // 清空分类标签
        this.elements.categoryTexts.forEach(text => {
            if (text && text.destroy) text.destroy();
        });
        this.elements.categoryTexts = [];

        // 如果是道具菜单，显示分类标签
        if (menuType === 'items' && controller) {
            this.showItemCategories(controller);
        }

        if (!options || options.length === 0) {
            this.showEmptyMenu(menuType, controller);
            return;
        }

        // 创建菜单选项文本
        options.forEach((option, index) => {
            let displayText = '';

            // 优先检查是否是返回选项
            if (option && (option.isReturn === true || option.name === '返回' || option.label === '返回')) {
                displayText = '返回';
            } else if (menuType === 'items' && typeof option === 'object' && option.name) {
                // 如果有 count 属性，显示叠加数量
                if (option.count && option.count > 1) {
                    displayText = `${option.name} * ${option.count}`;
                } else {
                    displayText = option.name;
                }
            } else if (menuType === 'status' && typeof option === 'object' && option.label) {
                displayText = option.label;
            } else if (typeof option === 'string') {
                displayText = option;
            } else if (typeof option === 'object' && option.name) {
                displayText = option.name;
            } else {
                displayText = '未知选项';
            }

            // 使用UIStyleHelper创建菜单项
            const text = UIStyleHelper.createMenuItem(
                this.scene,
                displayText,
                0,
                index * 30,
                index === selectedIndex
            );
            text.setOrigin(0, 0.5);

            // 添加鼠标交互
            if (onItemHover) {
                text.setInteractive({ useHandCursor: true });
                text.on('pointerover', () => onItemHover(index));
                text.on('pointerdown', () => {
                    onItemHover(index);
                    if (onItemClick) onItemClick(index);
                });
            }

            this.elements.optionTexts.push(text);
            this.elements.menuList.add(text);
        });

        // 居中菜单列表（如果有分类标签，需要向下偏移）
        const totalHeight = options.length * 30;
        const offsetY = this.elements.categoryTexts.length > 0 ? 50 : 0;
        this.elements.menuList.y = this.scene.cameras.main.centerY - (totalHeight / 2) + offsetY;
    }
    
    /**
     * 显示道具分类标签
     * @param {Object} controller - 菜单控制器
     */
    showItemCategories(controller) {
        const categories = controller.state.itemCategories;
        const currentCategory = controller.state.itemCategory;
        const startX = this.scene.cameras.main.width * 0.25;
        const startY = 80;
        const spacing = 10;
        let x = startX;
        
        categories.forEach((category, index) => {
            const isSelected = category.key === currentCategory;
            const color = UIStyleHelper.getMenuItemFill(isSelected, { isInactive: !isSelected });
            const fontSize = isSelected ? '16px' : '14px';
            
            const text = this.scene.add.text(
                x,
                startY,
                `[${category.name}]`,
                {
                    fontSize: fontSize,
                    fill: color,
                    fontFamily: 'monospace'
                }
            );
            text.setOrigin(0, 0.5);
            text.setDepth(10001);
            text.setScrollFactor(0);
            
            // 添加鼠标交互
            text.setInteractive({ useHandCursor: true });
            text.on('pointerdown', () => {
                controller.operation.switchItemCategory(category.key);
            });
            
            this.elements.categoryTexts.push(text);
            
            // 计算下一个标签的位置
            x += text.width + spacing;
        });
        
        // 添加提示文本
        const hintText = this.scene.add.text(
            startX,
            startY + 25,
            '← → 或 A/D 切换分类',
            {
                fontSize: '12px',
                fill: '#666666',
                fontFamily: 'monospace'
            }
        );
        hintText.setOrigin(0, 0.5);
        hintText.setDepth(10001);
        hintText.setScrollFactor(0);
        this.elements.categoryTexts.push(hintText);
    }

    /**
     * 更新选中项
     */
    updateSelection(selectedIndex) {
        if (this.elements.optionTexts && this.elements.optionTexts.length > 0) {
            this.elements.optionTexts.forEach((text, index) => {
                UIStyleHelper.applyMenuItemFill(text, index === selectedIndex);
            });
        }
    }

    /**
     * 显示空菜单
     */
    showEmptyMenu(menuType, controller = null) {
        const emptyText = this.scene.add.text(
            0,
            0,
            this.getEmptyMessage(menuType, controller),
            {
                fontSize: '16px',
                fill: '#888888',
                fontFamily: 'monospace',
                align: 'center'
            }
        );
        emptyText.setOrigin(0, 0.5);
        this.elements.menuList.add(emptyText);
        
        // 如果有分类标签，调整位置
        const offsetY = this.elements.categoryTexts.length > 0 ? 50 : 0;
        this.elements.menuList.y = this.scene.cameras.main.centerY + offsetY;
    }

    /**
     * 获取空菜单消息
     */
    getEmptyMessage(menuType, controller = null) {
        switch(menuType) {
            case 'items':
                if (controller && controller.state.itemCategory !== 'all') {
                    const categoryName = controller.state.itemCategories.find(
                        cat => cat.key === controller.state.itemCategory
                    )?.name || '该分类';
                    return `${categoryName}为空`;
                }
                return '道具栏为空';
            case 'equipment':
                return '无装备';
            case 'status':
                return '状态信息为空';
            default:
                return '菜单为空';
        }
    }

    /**
     * 更新信息面板
     */
    updateInfoPanel(menuType, option, playerData) {
        // 清空信息面板
        this.elements.infoPanel.removeAll(true);

        // 根据菜单类型显示不同的信息
        switch(menuType) {
            case 'main':
                this.addInfoText('选择菜单选项');
                this.addInfoText(UIStyleHelper.MENU_HINTS.NAVIGATE);
                this.addInfoText(UIStyleHelper.MENU_HINTS.SELECT);
                this.addInfoText(UIStyleHelper.MENU_HINTS.CANCEL);
                break;
            case 'items':
                if (option) {
                    // 如果物品是合并后的（有 items 数组），使用第一个实际物品来显示信息
                    const actualItem = option.items && option.items.length > 0 ? option.items[0] : option;
                    
                    this.addInfoText(`名称: ${option.name}`);
                    if (option.count && option.count > 1) {
                        this.addInfoText(`数量: ${option.count}`);
                    }
                    this.addInfoText(`类型: ${this.getItemTypeText(actualItem.type || '未知')}`);
                    if (actualItem.description) {
                        this.addInfoText(`描述: ${actualItem.description}`);
                    }
                    // 显示使用效果
                    const useDesc = ItemSystem.getUseDescription(actualItem);
                    if (useDesc) {
                        this.addInfoText(`使用效果: ${useDesc}`);
                    }
                    // 显示是否可以使用
                    const canUse = ItemSystem.canUseItem(actualItem, playerData);
                    this.addInfoText(canUse ? '空格/Enter 使用' : '当前无法使用');
                } else {
                    this.addInfoText('无道具信息');
                }
                break;
            case 'status':
                if (option) {
                    this.addInfoText(`${option.label}: ${option.value}`);
                }
                break;
        }
    }

    /**
     * 添加信息文本
     */
    addInfoText(text) {
        const infoItem = this.scene.add.text(
            0,
            this.elements.infoPanel.length * 25,
            text,
            {
                fontSize: '14px',
                fill: '#ffffff',
                fontFamily: 'monospace',
                align: 'left'
            }
        );
        infoItem.setOrigin(0, 0.5);
        this.elements.infoPanel.add(infoItem);
    }

    /**
     * 获取道具类型文本
     */
    getItemTypeText(type) {
        const typeMap = {
            'consumable': '消耗品',
            'tool': '工具',
            'key': '关键道具',
            'equipment': '装备',
            'weapon': '武器',
            'armor': '防具',
            'helmet': '头盔'
        };
        return typeMap[type] || type;
    }

    /**
     * 显示消息
     */
    showMessage(message, color = '#ffffff') {
        // 清除之前的消息
        if (this.messageText) {
            this.messageText.destroy();
        }

        // 创建消息文本（使用文本自带的背景色，保持原有透明风格）
        this.messageText = this.scene.add.text(
            this.scene.cameras.main.centerX,
            this.scene.cameras.main.centerY + 150,
            message,
            {
                fontSize: '16px',
                fill: color,
                fontFamily: 'monospace',
                backgroundColor: '#000000',
                padding: { x: 10, y: 5 }
            }
        ).setOrigin(0.5);
        this.messageText.setScrollFactor(0);
        this.messageText.setDepth(10002); // 确保在菜单之上（菜单是10001）

        // 2秒后自动消失
        this.scene.time.delayedCall(2000, () => {
            if (this.messageText) {
                this.messageText.destroy();
                this.messageText = null;
            }
        });
    }

    /**
     * 清理
     */
    destroy() {
        if (this.elements.menuList) {
            this.elements.menuList.removeAll(true);
        }
        if (this.elements.infoPanel) {
            this.elements.infoPanel.removeAll(true);
        }
        // 清理分类标签
        this.elements.categoryTexts.forEach(text => {
            if (text && text.destroy) text.destroy();
        });
        this.elements.categoryTexts = [];
        if (this.messageText) {
            this.messageText.destroy();
        }
    }
}

